import type { FormEvent } from "react";
import { AlertCircle, Plus, X } from "lucide-react";
import {
  CUSTOM_CASE_FIELD_LIMITS,
  CustomCaseFormData,
  CustomCaseFormErrors,
  CustomCaseSuspect,
} from "../../utils/customCase";

export type CustomCaseFormSection = "contact" | "details" | "suspects";

export interface CustomCaseDevSuspectPresetOption {
  key: string;
  label: string;
  description?: string;
}

export interface CustomCaseFormCopy {
  firstNameLabel: string;
  firstNamePlaceholder: string;
  firstNameHelp: string;
  emailLabel: string;
  emailPlaceholder: string;
  sourceLabel: string;
  sourcePlaceholder: string;
  sourceOptions: Array<{ value: string; label: string }>;
  cityLabel: string;
  cityPlaceholder: string;
  locationLabel: string;
  locationPlaceholder: string;
  suspectsLabel: string;
  suspectsHelp: string;
  suspectsMissing: string;
  devPresetTitle: string;
  devPresetDescription: string;
  suspectCardTitle: string;
  suspectMissingBadge: string;
  suspectFirstNameLabel: string;
  suspectFirstNamePlaceholder: string;
  ageLabel: string;
  agePlaceholder: string;
  eyesLabel: string;
  eyesOptions: Array<{ value: string; label: string }>;
  heightLabel: string;
  heightOptions: Array<{ value: string; label: string }>;
  sexLabel: string;
  sexOptions: Array<{ value: string; label: string }>;
  hairColorLabel: string;
  hairColorOptions: Array<{ value: string; label: string }>;
  hairLengthLabel: string;
  hairLengthOptions: Array<{ value: string; label: string }>;
  skinToneLabel: string;
  skinToneOptions: Array<{ value: string; label: string }>;
  occupationLabel: string;
  occupationPlaceholder: string;
  relationshipLabel: string;
  relationshipPlaceholder: string;
  notesLabel: string;
  notesPlaceholder: string;
  addSuspectEmpty: string;
  addSuspectMax: string;
  addSuspectButton: string;
  selectPlaceholder: string;
}

const DEFAULT_COPY: CustomCaseFormCopy = {
  firstNameLabel: "First Name",
  firstNamePlaceholder: "Your first name",
  firstNameHelp: "We use this in the welcome audio and delivery experience.",
  emailLabel: "Email",
  emailPlaceholder: "your@email.com",
  sourceLabel: "Where did you hear about us?",
  sourcePlaceholder: "Select an option",
  sourceOptions: [
    { value: "Google", label: "Google" },
    { value: "YouTube", label: "YouTube" },
    { value: "TikTok", label: "TikTok" },
    { value: "Instagram", label: "Instagram" },
    { value: "Pinterest", label: "Pinterest" },
    { value: "Other", label: "Other" },
  ],
  cityLabel: "City",
  cityPlaceholder: "Enter city name",
  locationLabel: "Location (optional)",
  locationPlaceholder: "Specific location within the city",
  suspectsLabel: "Suspects (Max 6)",
  suspectsHelp:
    "Enter suspects only. Do not enter the victim here. Use first names only, then add occupations, relationships, and appearance details.",
  suspectsMissing: "Please add at least one suspect before submitting.",
  devPresetTitle: "Dev Test Presets",
  devPresetDescription: "Replace the suspect list with one click for faster local testing.",
  suspectCardTitle: "Suspect",
  suspectMissingBadge: "Missing Details",
  suspectFirstNameLabel: "First Name",
  suspectFirstNamePlaceholder: "John",
  ageLabel: "Age",
  agePlaceholder: "30",
  eyesLabel: "Eyes",
  eyesOptions: [
    { value: "Light", label: "Light" },
    { value: "Dark", label: "Dark" },
  ],
  heightLabel: "Height",
  heightOptions: [
    { value: "Short", label: "Short" },
    { value: "Average", label: "Average" },
    { value: "Tall", label: "Tall" },
  ],
  sexLabel: "Sex",
  sexOptions: [
    { value: "M", label: "Male" },
    { value: "F", label: "Female" },
    { value: "N/A", label: "Prefer not to specify" },
  ],
  hairColorLabel: "Hair Color",
  hairColorOptions: [
    { value: "Black", label: "Black" },
    { value: "Brown", label: "Brown" },
    { value: "Blonde", label: "Blonde" },
    { value: "Red", label: "Red" },
    { value: "Gray", label: "Gray" },
    { value: "Bald", label: "Bald" },
    { value: "Other", label: "Other" },
  ],
  hairLengthLabel: "Hair Length",
  hairLengthOptions: [
    { value: "Short", label: "Short" },
    { value: "Medium", label: "Medium" },
    { value: "Long", label: "Long" },
    { value: "Bald", label: "Bald" },
  ],
  skinToneLabel: "Skin Tone",
  skinToneOptions: [
    { value: "Light", label: "Light" },
    { value: "Medium", label: "Medium" },
    { value: "Dark", label: "Dark" },
    { value: "Other", label: "Other" },
    { value: "Prefer not to specify", label: "Prefer not to specify" },
  ],
  occupationLabel: "Occupation",
  occupationPlaceholder: "Software Developer",
  relationshipLabel: "Relationship (optional)",
  relationshipPlaceholder: "Friend of the victim",
  notesLabel: "Extra Notes (optional)",
  notesPlaceholder: "Anything else that helps the story feel personal.",
  addSuspectEmpty: "Add at least one suspect. You can include up to six.",
  addSuspectMax: "Maximum number of suspects reached (6)",
  addSuspectButton: "Add Suspect",
  selectPlaceholder: "Select...",
};

interface CustomCaseFormFieldsProps {
  formData: CustomCaseFormData;
  errors: CustomCaseFormErrors;
  disabled?: boolean;
  sections?: CustomCaseFormSection[];
  copy?: CustomCaseFormCopy;
  submitLabel?: string;
  canAddSuspect?: boolean;
  addSuspectHint?: string;
  suspectPresetOptions?: CustomCaseDevSuspectPresetOption[];
  onApplySuspectPreset?: (presetKey: string) => void;
  onSubmit?: (event: FormEvent<HTMLFormElement>) => void;
  onFormDataChange: (updater: (current: CustomCaseFormData) => CustomCaseFormData) => void;
  onAddSuspect: () => void;
  onRemoveSuspect: (index: number) => void;
  onSuspectChange: (index: number, field: keyof CustomCaseSuspect, value: string) => void;
}

const hasSuspectError = (
  errors: CustomCaseFormErrors,
  index: number,
  field: keyof CustomCaseSuspect
) => Array.isArray(errors.suspects) && errors.suspects.some((error) => error.index === index && error.field === field);

const getSuspectErrorMessage = (
  errors: CustomCaseFormErrors,
  index: number,
  field: keyof CustomCaseSuspect
) =>
  Array.isArray(errors.suspects)
    ? errors.suspects.find((error) => error.index === index && error.field === field)?.message || ""
    : "";

const hasGeneralSuspectError = (errors: CustomCaseFormErrors) =>
  Array.isArray(errors.suspects) && errors.suspects.some((error) => error.field === "general");

const getFieldClassName = (hasError: boolean, isEmpty = false) =>
  [
    "w-full rounded-md border bg-background px-3 py-2 transition focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:opacity-60",
    hasError
      ? "border-red-500/85 ring-1 ring-red-500/35 focus:border-red-400 focus:ring-red-400/45"
      : "border-border focus:ring-primary",
    isEmpty ? "text-muted-foreground/80" : "text-foreground",
    "placeholder:text-muted-foreground/45",
  ].join(" ");

const getCardClassName = (hasError: boolean) =>
  [
    "relative rounded-lg p-4 transition",
    hasError
      ? "border border-red-500/45 bg-red-500/5 shadow-[0_0_0_1px_rgba(239,68,68,0.08)]"
      : "border border-border/70 bg-muted/55",
  ].join(" ");

const CustomCaseFormFields = ({
  formData,
  errors,
  disabled = false,
  sections = ["contact", "details", "suspects"],
  copy = DEFAULT_COPY,
  submitLabel = "Save and Continue",
  canAddSuspect = true,
  addSuspectHint = "",
  suspectPresetOptions = [],
  onApplySuspectPreset,
  onSubmit,
  onFormDataChange,
  onAddSuspect,
  onRemoveSuspect,
  onSuspectChange,
}: CustomCaseFormFieldsProps) => {
  const showSection = (section: CustomCaseFormSection) => sections.includes(section);

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      {showSection("contact") ? (
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="firstName" className="mb-2 block text-sm font-medium">
              {copy.firstNameLabel} <span className="text-destructive dark:text-white">*</span>
            </label>
            <input
              type="text"
              id="firstName"
              disabled={disabled}
              value={formData.firstName}
              onChange={(event) =>
                onFormDataChange((current) => ({ ...current, firstName: event.target.value }))
              }
              className={getFieldClassName(Boolean(errors.firstName), !formData.firstName)}
              placeholder={copy.firstNamePlaceholder}
              maxLength={CUSTOM_CASE_FIELD_LIMITS.firstName}
              required
            />
            {errors.firstName ? (
              <p className="mt-1 flex items-center gap-1 text-sm text-destructive">
                <AlertCircle className="h-4 w-4" />
                {errors.firstName}
              </p>
            ) : (
              <p className="mt-1 text-xs text-muted-foreground">
                {copy.firstNameHelp}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="email" className="mb-2 block text-sm font-medium">
              {copy.emailLabel} <span className="text-destructive dark:text-white">*</span>
            </label>
            <input
              type="email"
              id="email"
              disabled={disabled}
              value={formData.email}
              onChange={(event) =>
                onFormDataChange((current) => ({ ...current, email: event.target.value }))
              }
              className={getFieldClassName(Boolean(errors.email), !formData.email)}
              placeholder={copy.emailPlaceholder}
              required
            />
            {errors.email ? (
              <p className="mt-1 flex items-center gap-1 text-sm text-destructive">
                <AlertCircle className="h-4 w-4" />
                {errors.email}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}

      {showSection("details") ? (
        <>
          <div>
            <label htmlFor="source" className="mb-2 block text-sm font-medium">
              {copy.sourceLabel} <span className="text-destructive dark:text-white">*</span>
            </label>
            <select
              id="source"
              disabled={disabled}
              value={formData.source}
              onChange={(event) =>
                onFormDataChange((current) => ({ ...current, source: event.target.value }))
              }
              className={getFieldClassName(Boolean(errors.source), !formData.source)}
              required
            >
              <option value="">{copy.sourcePlaceholder}</option>
              {copy.sourceOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {errors.source ? (
              <p className="mt-1 flex items-center gap-1 text-sm text-destructive">
                <AlertCircle className="h-4 w-4" />
                {errors.source}
              </p>
            ) : null}
          </div>

          <div>
            <label htmlFor="city" className="mb-2 block text-sm font-medium">
              {copy.cityLabel} <span className="text-destructive dark:text-white">*</span>
            </label>
            <input
              type="text"
              id="city"
              disabled={disabled}
              value={formData.city}
              onChange={(event) =>
                onFormDataChange((current) => ({ ...current, city: event.target.value }))
              }
              className={getFieldClassName(Boolean(errors.city), !formData.city)}
              placeholder={copy.cityPlaceholder}
              maxLength={CUSTOM_CASE_FIELD_LIMITS.city}
              required
            />
            {errors.city ? (
              <p className="mt-1 flex items-center gap-1 text-sm text-destructive">
                <AlertCircle className="h-4 w-4" />
                {errors.city}
              </p>
            ) : null}
          </div>

          <div>
            <label htmlFor="location" className="mb-2 block text-sm font-medium">
              {copy.locationLabel}
            </label>
            <input
              type="text"
              id="location"
              disabled={disabled}
              value={formData.location}
              onChange={(event) =>
                onFormDataChange((current) => ({ ...current, location: event.target.value }))
              }
              className={getFieldClassName(Boolean(errors.location), !formData.location)}
              placeholder={copy.locationPlaceholder}
              maxLength={CUSTOM_CASE_FIELD_LIMITS.location}
            />
            {errors.location ? (
              <p className="mt-1 flex items-center gap-1 text-sm text-destructive">
                <AlertCircle className="h-4 w-4" />
                {errors.location}
              </p>
            ) : null}
          </div>
        </>
      ) : null}

      {showSection("suspects") ? (
        <div>
        <div className="mb-2">
          <label className="block text-sm font-medium">
            {copy.suspectsLabel} <span className="text-destructive dark:text-white">*</span>
          </label>
          <p className="mt-1 text-xs text-muted-foreground">
            {copy.suspectsHelp}
          </p>
          {hasGeneralSuspectError(errors) ? (
            <p className="mt-3 flex items-center gap-1 text-sm text-destructive">
              <AlertCircle className="h-4 w-4" />
              {copy.suspectsMissing}
            </p>
          ) : null}
        </div>

        {suspectPresetOptions.length > 0 && onApplySuspectPreset ? (
          <div className="mb-4 rounded-lg border border-dashed border-border/70 bg-muted/35 p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-medium">{copy.devPresetTitle}</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  {copy.devPresetDescription}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {suspectPresetOptions.map((preset) => (
                  <button
                    key={preset.key}
                    type="button"
                    onClick={() => onApplySuspectPreset(preset.key)}
                    disabled={disabled}
                    className="rounded-full border border-border/80 bg-background px-3 py-1.5 text-xs font-semibold text-foreground transition hover:bg-accent hover:text-accent-foreground disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>
            {suspectPresetOptions.some((preset) => preset.description) ? (
              <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-muted-foreground">
                {suspectPresetOptions.map((preset) =>
                  preset.description ? (
                    <span
                      key={`${preset.key}-description`}
                      className="rounded-full border border-border/60 px-2.5 py-1"
                    >
                      {preset.label}: {preset.description}
                    </span>
                  ) : null
                )}
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="space-y-6">
          {formData.suspects.map((suspect, index) => {
            const suspectHasError =
              Array.isArray(errors.suspects) && errors.suspects.some((error) => error.index === index);

            return (
            <div key={index} className={getCardClassName(Boolean(suspectHasError))}>
              <button
                type="button"
                onClick={() => onRemoveSuspect(index)}
                disabled={disabled}
                className="absolute right-2 top-2 rounded-md p-1 text-destructive hover:bg-destructive/10 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <X className="h-4 w-4" />
              </button>
              <div className="mb-3 flex items-center justify-between pr-8">
                <h3 className="font-medium">{copy.suspectCardTitle} {index + 1}</h3>
                {suspectHasError ? (
                  <span className="rounded-full border border-red-500/30 bg-red-500/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-red-200">
                    {copy.suspectMissingBadge}
                  </span>
                ) : null}
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium">
                    {copy.suspectFirstNameLabel} <span className="text-destructive dark:text-white">*</span>
                  </label>
                  <input
                    type="text"
                    disabled={disabled}
                    value={suspect.fullName}
                    onChange={(event) => onSuspectChange(index, "fullName", event.target.value)}
                    className={getFieldClassName(hasSuspectError(errors, index, "fullName"), !suspect.fullName)}
                    placeholder={copy.suspectFirstNamePlaceholder}
                    maxLength={CUSTOM_CASE_FIELD_LIMITS.suspectFullName}
                    required
                  />
                  {hasSuspectError(errors, index, "fullName") ? (
                    <p className="mt-1 flex items-center gap-1 text-sm text-destructive">
                      <AlertCircle className="h-4 w-4" />
                      {getSuspectErrorMessage(errors, index, "fullName") || "First name is required"}
                    </p>
                  ) : null}
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium">
                    {copy.ageLabel} <span className="text-destructive dark:text-white">*</span>
                  </label>
                  <input
                    type="text"
                    disabled={disabled}
                    value={suspect.age}
                    onChange={(event) => onSuspectChange(index, "age", event.target.value)}
                    className={getFieldClassName(hasSuspectError(errors, index, "age"), !suspect.age)}
                    placeholder={copy.agePlaceholder}
                    maxLength={CUSTOM_CASE_FIELD_LIMITS.age}
                  />
                  {hasSuspectError(errors, index, "age") ? (
                    <p className="mt-1 flex items-center gap-1 text-sm text-destructive">
                      <AlertCircle className="h-4 w-4" />
                      {getSuspectErrorMessage(errors, index, "age") || "Age is required"}
                    </p>
                  ) : null}
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium">{copy.eyesLabel}</label>
                  <select
                    disabled={disabled}
                    value={suspect.eyes}
                    onChange={(event) => onSuspectChange(index, "eyes", event.target.value)}
                    className={getFieldClassName(false, !suspect.eyes)}
                  >
                    <option value="">{copy.selectPlaceholder}</option>
                    {copy.eyesOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium">{copy.heightLabel}</label>
                  <select
                    disabled={disabled}
                    value={suspect.height}
                    onChange={(event) => onSuspectChange(index, "height", event.target.value)}
                    className={getFieldClassName(false, !suspect.height)}
                  >
                    <option value="">{copy.selectPlaceholder}</option>
                    {copy.heightOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium">
                    {copy.sexLabel} <span className="text-destructive dark:text-white">*</span>
                  </label>
                  <select
                    disabled={disabled}
                    value={suspect.sex}
                    onChange={(event) => onSuspectChange(index, "sex", event.target.value)}
                    className={getFieldClassName(hasSuspectError(errors, index, "sex"), !suspect.sex)}
                  >
                    <option value="">{copy.selectPlaceholder}</option>
                    {copy.sexOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {hasSuspectError(errors, index, "sex") ? (
                    <p className="mt-1 flex items-center gap-1 text-sm text-destructive">
                      <AlertCircle className="h-4 w-4" />
                      {getSuspectErrorMessage(errors, index, "sex") || "Sex is required"}
                    </p>
                  ) : null}
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium">
                    {copy.hairColorLabel} <span className="text-destructive dark:text-white">*</span>
                  </label>
                  <select
                    disabled={disabled}
                    value={suspect.hairColor}
                    onChange={(event) => onSuspectChange(index, "hairColor", event.target.value)}
                    className={getFieldClassName(hasSuspectError(errors, index, "hairColor"), !suspect.hairColor)}
                  >
                    <option value="">{copy.selectPlaceholder}</option>
                    {copy.hairColorOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {hasSuspectError(errors, index, "hairColor") ? (
                    <p className="mt-1 flex items-center gap-1 text-sm text-destructive">
                      <AlertCircle className="h-4 w-4" />
                      {getSuspectErrorMessage(errors, index, "hairColor") || "Hair color is required"}
                    </p>
                  ) : null}
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium">{copy.hairLengthLabel}</label>
                  <select
                    disabled={disabled}
                    value={suspect.hairLength}
                    onChange={(event) => onSuspectChange(index, "hairLength", event.target.value)}
                    className={getFieldClassName(false, !suspect.hairLength)}
                  >
                    <option value="">{copy.selectPlaceholder}</option>
                    {copy.hairLengthOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium">
                    {copy.skinToneLabel} <span className="text-destructive dark:text-white">*</span>
                  </label>
                  <select
                    disabled={disabled}
                    value={suspect.skinTone}
                    onChange={(event) => onSuspectChange(index, "skinTone", event.target.value)}
                    className={getFieldClassName(hasSuspectError(errors, index, "skinTone"), !suspect.skinTone)}
                  >
                    <option value="">{copy.selectPlaceholder}</option>
                    {copy.skinToneOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {hasSuspectError(errors, index, "skinTone") ? (
                    <p className="mt-1 flex items-center gap-1 text-sm text-destructive">
                      <AlertCircle className="h-4 w-4" />
                      {getSuspectErrorMessage(errors, index, "skinTone") || "Skin tone is required"}
                    </p>
                  ) : null}
                </div>

                <div className="sm:col-span-2">
                  <label className="mb-1 block text-sm font-medium">
                    {copy.occupationLabel} <span className="text-destructive dark:text-white">*</span>
                  </label>
                  <input
                    type="text"
                    disabled={disabled}
                    value={suspect.occupation}
                    onChange={(event) => onSuspectChange(index, "occupation", event.target.value)}
                    className={getFieldClassName(hasSuspectError(errors, index, "occupation"), !suspect.occupation)}
                    placeholder={copy.occupationPlaceholder}
                    maxLength={CUSTOM_CASE_FIELD_LIMITS.occupation}
                  />
                  {hasSuspectError(errors, index, "occupation") ? (
                    <p className="mt-1 flex items-center gap-1 text-sm text-destructive">
                      <AlertCircle className="h-4 w-4" />
                      {getSuspectErrorMessage(errors, index, "occupation") || "Occupation is required"}
                    </p>
                  ) : null}
                </div>

                <div className="sm:col-span-2">
                  <label className="mb-1 block text-sm font-medium">{copy.relationshipLabel}</label>
                  <input
                    type="text"
                    disabled={disabled}
                    value={suspect.relationship}
                    onChange={(event) => onSuspectChange(index, "relationship", event.target.value)}
                    className={getFieldClassName(hasSuspectError(errors, index, "relationship"), !suspect.relationship)}
                    placeholder={copy.relationshipPlaceholder}
                    maxLength={CUSTOM_CASE_FIELD_LIMITS.relationship}
                  />
                  {hasSuspectError(errors, index, "relationship") ? (
                    <p className="mt-1 flex items-center gap-1 text-sm text-destructive">
                      <AlertCircle className="h-4 w-4" />
                      {getSuspectErrorMessage(errors, index, "relationship")}
                    </p>
                  ) : null}
                </div>

                <div className="sm:col-span-2">
                  <label className="mb-1 block text-sm font-medium">{copy.notesLabel}</label>
                  <textarea
                    disabled={disabled}
                    value={suspect.notes}
                    onChange={(event) => onSuspectChange(index, "notes", event.target.value)}
                    className={[
                      "min-h-24",
                      getFieldClassName(hasSuspectError(errors, index, "notes"), !suspect.notes),
                    ].join(" ")}
                    placeholder={copy.notesPlaceholder}
                    maxLength={CUSTOM_CASE_FIELD_LIMITS.notes}
                  />
                  {hasSuspectError(errors, index, "notes") ? (
                    <p className="mt-1 flex items-center gap-1 text-sm text-destructive">
                      <AlertCircle className="h-4 w-4" />
                      {getSuspectErrorMessage(errors, index, "notes")}
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
          )})}
        </div>

        <div className="mt-4 flex flex-col items-end space-y-4">
          {formData.suspects.length === 0 ? (
              <p className="self-center text-sm text-muted-foreground">
              {copy.addSuspectEmpty}
            </p>
          ) : formData.suspects.length >= 6 ? (
            <p className="self-center text-sm text-muted-foreground">
              {copy.addSuspectMax}
            </p>
          ) : !canAddSuspect && addSuspectHint ? (
            <p className="self-center text-sm text-destructive">{addSuspectHint}</p>
          ) : null}

          <button
            type="button"
            onClick={onAddSuspect}
            disabled={disabled || formData.suspects.length >= 6 || !canAddSuspect}
            className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Plus className="h-5 w-5" />
            {copy.addSuspectButton}
          </button>
        </div>
      </div>
      ) : null}

      {onSubmit ? (
        <button
          type="submit"
          disabled={disabled}
          className="w-full rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submitLabel}
        </button>
      ) : null}
    </form>
  );
};

export default CustomCaseFormFields;
